// @flow

// Libs
import _escapeRegExp from 'lodash/escapeRegExp'
import _every from 'lodash/every'
import _orderBy from 'lodash/orderBy'
import _partition from 'lodash/partition'
import { createSelector } from 'reselect'

// Utils
import emptyArrayOr from 'fc-utils/emptyArrayOr'
import mobileDevice from 'fc-utils/deviceCheck'

// Constants
import {
  SORT_AS_DATE,
  SORT_AS_MIX,
  SORT_AS_NUMBER,
  SORT_AS_BOOLEAN,
  SORT_AS_GROUP,
  SORT_AS_STRING,
  ASC,
  DESC,
} from './constants'

// Types
import type {
  DataTableState,
  DataSelector,
  MakeRecordsSelector,
  DataTableSelectors,
} from '../../types'

type TableStateSelector<T> = (state: Object) => T

function makeTableStateSelector<T> (
  tableState$: (state: Object) => DataTableState,
  path: string
): TableStateSelector<T> {
  return function (state: Object): T {
    const tableState = tableState$(state)
    return tableState[path]
  }
}

export default function composeSelectors<M, R: Object> ({
  data$,
  tableState$,
  makeRecords,
  }: {
  data$: DataSelector<M>,
  tableState$: (state: Object) => DataTableState,
  makeRecords: MakeRecordsSelector<M, R>
}): DataTableSelectors<M, R> {
  //
  const records$: DataSelector<R> = makeRecords(data$)
  const page$ = (makeTableStateSelector(tableState$, 'page'): TableStateSelector<number>)
  const perPage$ = mobileDevice ? (): number => 25
    : (makeTableStateSelector(tableState$, 'perPage'): TableStateSelector<number>)
  const sortBy$ = (makeTableStateSelector(tableState$, 'sortBy'): TableStateSelector<?string>)
  const sortAs$ = (makeTableStateSelector(tableState$, 'sortAs'): TableStateSelector<?string>)
  const sortDesc$ = (makeTableStateSelector(tableState$, 'sortDesc'): TableStateSelector<boolean>)
  const booleanAttr$ = (makeTableStateSelector(tableState$, 'booleanAttr'): TableStateSelector<?string>)
  const groupBy$ = (makeTableStateSelector(tableState$, 'groupBy'): TableStateSelector<?string>)
  const groupOrder$ = (makeTableStateSelector(tableState$, 'groupOrder'): TableStateSelector<?Array<string | number>>)
  const filters$ = (makeTableStateSelector(tableState$, 'filters'): TableStateSelector<{[string]: string}>)
  const selectedRecordIds$ = (makeTableStateSelector(tableState$, 'selectedRecords'): TableStateSelector<Array<number>>)

  const filteredRecords$: (state: Object) => Array<R> = createSelector(
    [records$, filters$],
    function (records: Array<R>, filters: {[string]: string}): Array<R> {
      if (Object.keys(filters).length) {
        const filtered = records.filter((record: R): boolean => _every(filters,
          (filterBy: string, attr: string): boolean => {
            const value = String(record[attr])

            if (value) {
              try {
                const [, maybeRegExp] = (filterBy.match(/^\/(.+)\/$/) || [])
                if (maybeRegExp) {
                  return !!(value.match(new RegExp(maybeRegExp, 'ig')))
                }

                return !!(value.match(new RegExp(_escapeRegExp(filterBy), 'ig')))
              } catch (error) {
                return false
              }
            }

            return false
          })
        )

        return emptyArrayOr(filtered)
      }

      return emptyArrayOr(records)
    }
  )

  const sortedRecords$: (state: Object) => Array<R> = createSelector(
    [filteredRecords$, sortBy$, sortAs$, sortDesc$, booleanAttr$, groupBy$, groupOrder$],
    function (
      records: Array<R>,
      sortBy: ?string,
      sortAs: ?string,
      sortDesc: boolean,
      booleanAttr: ?string,
      groupBy: string,
      groupOrder: ?Array<string | number> = [],
    ): Array<R> {
      if (sortBy) {
        const order = sortDesc ? DESC : ASC
        let sorted

        if (sortAs !== SORT_AS_MIX) {
          sorted = _orderBy(records, (record: R): * => {
            let value = record[sortBy]

            switch (sortAs) {
              case SORT_AS_DATE:
                return new Date(value)
              case SORT_AS_NUMBER:
                return +value
              case SORT_AS_BOOLEAN:
                return record[booleanAttr]
              case SORT_AS_GROUP:
                return groupOrder.indexOf(record[groupBy])
              case SORT_AS_STRING:
              default:
                return String(value).toLowerCase()
            }
          }, [order])
        } else {
          const partition = _partition(records, (record: R): boolean => (/^[a-z]+\s*[a-z]*$/gi).test(record[sortBy]))
          const stringValArr = partition[0]
          const numericValArr = partition[1]
          const sortedStringArr: Array = _orderBy(stringValArr, [sortBy], [order])
          const sortedNumericArr: Array = _orderBy(numericValArr, (record: R): number =>
            parseFloat(record[sortBy]), [order]
          )

          sorted = order === ASC ? sortedStringArr.concat(sortedNumericArr) : sortedNumericArr.concat(sortedStringArr)
        }

        return emptyArrayOr(sorted)
      }

      return emptyArrayOr(records)
    }
  )

  const finalRecords$ = sortedRecords$

  const finalSelectedRecordIds = createSelector(
    [selectedRecordIds$, filteredRecords$],
    function (recordIds: Array<number>, records: Array<R>): Array<number> {
      const allRecordIds = records.map((record: R): number => record['id'])
      const filtered = recordIds.filter((id: number): boolean => allRecordIds.indexOf(id) >= 0)

      return emptyArrayOr(filtered)
    }
  )

  const max$: (state: Object) => number = createSelector(
    [finalRecords$],
    function (records: Array<R>): number {
      return records.length
    }
  )

  const pages$: (state: Object) => Array<Array<R>> = createSelector(
    [finalRecords$, page$, perPage$],
    function (records: Array<R>, page: number, perPage: number): Array<Array<R>> {
      const max = records.length
      const numberOfPages = Math.ceil(max / perPage)

      const chunks = []
      for (let i = 0; i < numberOfPages; i++) {
        if (i === page) {
          const start = i * perPage
          const end = start + perPage
          chunks[i] = records.slice(start, end)
        } else {
          chunks[i] = []
        }
      }

      return emptyArrayOr(chunks)
    }
  )

  const pageRecords$: (state: Object) => Array<R> = createSelector(
    [pages$, page$],
    function (pages: Array<Array<R>>, page: number): Array<R> {
      const pageRecords: Array<R> = (pages[page] || [])

      return emptyArrayOr(pageRecords)
    }
  )

  return {
    data$,
    records$: filteredRecords$,
    selectedRecordIds$: finalSelectedRecordIds,
    page$,
    pages$,
    perPage$,
    pageRecords$,
    max$,
    sortBy$,
    sortDesc$,
    filters$,
  }
}
